import RNEventSource from 'react-native-event-source';
import { EventSourcePolyfill } from 'event-source-polyfill';
import Config from 'react-native-config';
import Logger, { LoggingModule } from 'services/Logger';
import { normalizedFhirTimeSlots } from 'store/booking/mappers';
import {
  GET_LOCATION_TIME_SLOTS_SUCCESS,
  GET_NPV2_LOCATION_TIME_SLOTS_SUCCESS
} from 'store/booking/appointment/timeSlots/actions';
import { SseConnection, SseListeners, SsePlatforms } from 'services/ServerSentEvents';

export const KEY = 'SSE';
export const TRIGGER_SSE_UPDATES = `${KEY}.TRIGGER_SSE_UPDATES`;

export class EventSourceService {
  private connection: SseConnection = { eventSource: null };
  platform: SsePlatforms;

  constructor(platform: SsePlatforms) {
    this.platform = platform;
  }

  /**
   * Closes the apps given SSE connection
   */
  closeConnection() {
    if (this.connection.eventSource) {
      // Since we're only using SSE for slots, we're removing all listeners and closing the connection
      // In the future we may need another function that removes a single listener instead of removing all and closing the connection
      if (this.platform === SsePlatforms.MOBILE) this.connection.eventSource.removeAllListeners();
      this.connection.eventSource.close();
      this.connection.eventSource = null;
      Logger.debug(`${LoggingModule.booking} closed sse connection`);
    }
  }

  /**
   * Instantiates a connection to the Mercure Hub
   * @param uri the topic url
   * @param accessToken The access token to auth with the hub. Not the Ping/Cerner FHIR token.
   */
  async initConnection(uri: string, accessToken: string) {
    if (!this.connection.eventSource) {
      const options = {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      };

      if (this.platform === SsePlatforms.MOBILE) {
        this.connection.eventSource = new RNEventSource(
          `${Config.MERCURE_HUB_URL}?topic=${uri}`,
          options
        );
      } else {
        this.connection.eventSource = new EventSourcePolyfill(
          `${Config.MERCURE_HUB_URL}?topic=${uri}`,
          options
        );
      }

      this.addDefaultListeners(this.connection.eventSource);
    }
  }

  /**
   * Adds default listeners to the SSE connection
   * @param eventSource the apps current SSE connection
   */
  private addDefaultListeners(eventSource: any) {
    eventSource.addEventListener('open', () => {
      Logger.debug(`${LoggingModule.booking} Opened SSE connection`);
    });
    // this is required by the polyfill
    eventSource.addEventListener('message', () => {});
    eventSource.addEventListener('error', (event: any) => {
      Logger.error(`${LoggingModule.booking} sse error: ${event.data}`);
    });
  }

  /**
   * Adds a custom listener to the given SSE connection
   * @param listener the custom listener to add
   * @param dispatch
   */
  addCustomListener(listener: SseListeners, dispatch: Function) {
    if (this.connection.eventSource) {
      switch (listener) {
        case SseListeners.SLOTS:
          this.connection.eventSource.addEventListener(`${listener}`, (event: any) => {
            const normalizedSlots = normalizedFhirTimeSlots(JSON.parse(event.data));
            dispatch({ type: GET_LOCATION_TIME_SLOTS_SUCCESS, payload: normalizedSlots });
          });
          break;
        default:
          Logger.error(`Listener type: ${listener} is not supported`);
          break;
      }
    }
  }

  /**
   * Adds a custom listener to the given SSE connection on NPV2 flow
   * @param listener the custom listener to add
   * @param dispatch
   */
  addCustomNPV2Listener(listener: SseListeners, dispatch: Function) {
    if (this.connection.eventSource) {
      switch (listener) {
        case SseListeners.SLOTS:
          this.connection.eventSource.addEventListener(`${listener}`, (event: any) => {
            const normalizedSlots = normalizedFhirTimeSlots(JSON.parse(event.data));
            dispatch({ type: GET_NPV2_LOCATION_TIME_SLOTS_SUCCESS, payload: normalizedSlots });
          });
          break;
        default:
          Logger.error(`Listener type: ${listener} is not supported`);
          break;
      }
    }
  }
}
